import { useCallback, useRef, Dispatch, SetStateAction } from "react";
import { sectionDataObject, deleteObject, updateObject } from "@/lib/stores/objectsStore";
import { createTemplate } from "@/lib/stores/templatesStore";
import { GlossaryBlueprint } from "@/types-games";
import {
  getChangeSummary,
  BlueprintVersionChanges,
} from "@/lib/utils/blueprintVersionUtils";
import {
  extractBlueprintVersion,
  formatVersionForDisplay,
  getBlueprintVersionForSaving,
} from "@/lib/utils/versionUtils";
import { rebuildGlossaryItemFromBlueprint, parseGlossaryItemData } from "@/lib/utils/objectParser";
import { WizardMode, ViewDisplayMode } from "../types";

interface UseObjectWizardHandlersProps {
  // State setters
  setViewDisplayMode: Dispatch<SetStateAction<ViewDisplayMode>>;
  setWizardMode: Dispatch<SetStateAction<WizardMode>>;
  setPendingAction: Dispatch<SetStateAction<"close" | "cancel" | null>>;
  setShowUnsavedDialog: Dispatch<SetStateAction<boolean>>;
  setCurrentItemBlueprintVersion: Dispatch<SetStateAction<number | { major: number; minor: number; patch: number } | undefined>>;
  setBlueprintChanges: Dispatch<SetStateAction<BlueprintVersionChanges | null>>;

  // Data
  glossaryState: any;
  wizardMode: WizardMode;
  inline: boolean;
  initialData: any;
  gameId: any;
  glossaryBlueprints: GlossaryBlueprint[];
  blueprintChanges: BlueprintVersionChanges | null;
  canEdit: boolean;

  // Callbacks
  toast: any;
  confirmation: any;
  onClose?: () => void;
  onSave?: (data: any) => void;
  onUpdate?: (data: any) => void;
  onUnsavedChangesChange?: (hasChanges: boolean, saveHandler: () => Promise<void>, discardHandler?: () => void) => void;
  unsavedChanges: any;
  // Draft management
  clearDraft?: () => void;
}

/**
 * Hook that provides all event handlers for the ObjectWizard
 * Consolidates save, delete, cancel, and other operations
 */
export function useObjectWizardHandlers({
  setViewDisplayMode,
  setWizardMode,
  setPendingAction,
  setShowUnsavedDialog,
  setCurrentItemBlueprintVersion,
  setBlueprintChanges,
  glossaryState,
  wizardMode,
  inline,
  initialData,
  gameId,
  glossaryBlueprints,
  blueprintChanges,
  canEdit,
  toast,
  confirmation,
  onClose,
  onSave,
  onUpdate,
  onUnsavedChangesChange,
  unsavedChanges,
  clearDraft,
}: UseObjectWizardHandlersProps) {
  // Store save handler in ref for stable callback
  const saveHandlerRef = useRef<() => Promise<void>>();

  /**
   * Switches wizard to edit mode without rebuilding from blueprint
   * This preserves the object's current data structure, even if it's out of sync with the template
   */
  const handleEdit = useCallback(() => {
    if (!canEdit) {
      return; // Don't allow editing if user doesn't have permission
    }
    // Switch back to structured view first
    setViewDisplayMode("structured");
    setWizardMode("edit");

    // DO NOT rebuild from blueprint when entering edit mode
    // This allows users to edit the object as-is, even when there's a version mismatch
    // The object will keep its old structure and blueprint_version until migration is explicitly applied
  }, [canEdit, setViewDisplayMode, setWizardMode]);

  /**
   * Updates the glossary item title
   */
  const handleTitleChange = useCallback((newTitle: string) => {
    glossaryState.setItemTitle(newTitle);
  }, [glossaryState]);

  /**
   * Handles removing fields from the glossary item when blueprint mismatch occurs
   */
  const handleRemoveFields = useCallback((fieldIds: string[]) => {
    // Remove the specified fields from the parsed sections
    const updatedSections = glossaryState.parsedSections
      .map((section: sectionDataObject) => ({
        ...section,
        fields: section.fields.filter((field) => !fieldIds.includes(field.id)),
      }))
      .filter((section: sectionDataObject) => section.fields.length > 0); // Remove empty sections

    // Update the glossary state
    glossaryState.updateSections(updatedSections);

    toast({
      title: "Fields Removed",
      description: `${fieldIds.length} field(s) have been removed from the object.`,
    });
  }, [glossaryState, toast]);

  /**
   * Handles creating a new blueprint with the current glossary item structure
   */
  const handleCreateNewBlueprint = useCallback(async (
    blueprintName: string,
    fieldIds: string[]
  ) => {
    if (!gameId?.id || !glossaryState.selectedBlueprint) {
      toast({
        title: "Error",
        description: "Missing game ID or blueprint data",
        variant: "destructive",
      });
      return;
    }

    try {
      // Create a new blueprint data structure based on the current glossary item structure
      // This ensures the blueprint matches exactly what the glossary item contains
      const currentBlueprintData = glossaryState.selectedBlueprint.data;
      const newBlueprintData = {
        sections: {} as Record<string, any>,
        devnotes: currentBlueprintData.devnotes || {},
        elementOrder: [] as string[],
      };

      // Build the blueprint structure from the glossary item's parsed sections
      glossaryState.parsedSections.forEach((section: sectionDataObject, sectionIndex: number) => {
        const sectionId = section.id;

        // Create section data
        newBlueprintData.sections[sectionId] = {
          id: sectionId,
          title: section.title,
          fieldIds: section.fields.map((field) => field.id),
          order: sectionIndex,
        };

        // Add section to element order
        newBlueprintData.elementOrder.push(sectionId);

        // Create field definitions for each field in the section
        section.fields.forEach((field, fieldIndex) => {
          const fieldId = field.id;

          // Create field definition based on the actual field data
          newBlueprintData.sections[`field_${fieldId}`] = {
            id: fieldId,
            type: field.type,
            label: field.label,
            required: field.required || false,
            translation: field.translation || false,
            multiple: field.multiple || false,
            filter: field.filter || false,
            filterOptions: field.filterOptions || [],
            order: fieldIndex,
            sectionId: sectionId,
          };

          // Add field to element order
          newBlueprintData.elementOrder.push(`field_${fieldId}`);
        });
      });

      // Create the new blueprint
      const newBlueprint = await createTemplate(
        {
          game_id: gameId.id,
          name: blueprintName,
          tags: glossaryState.selectedBlueprint.tags || [],
          type: glossaryState.selectedBlueprint.type || "glossary",
          description: `${glossaryState.selectedBlueprint.description || ""
            } (Modified to preserve fields)`,
          data: newBlueprintData,
          settings: glossaryState.selectedBlueprint.settings || {},
          version: {
            changes: { preservedFields: fieldIds },
            version: 0.00001,
          },
          folder: glossaryState.selectedBlueprint.folder || null,
        },
        toast
      );

      // Update the glossary item to use the new blueprint
      glossaryState.setSelectedBlueprint(newBlueprint);

      // Update the glossary item data to reference the new blueprint
      if (initialData) {
        // Get the blueprint version in the correct format
        const newBlueprintVersion = getBlueprintVersionForSaving(newBlueprint);

        const updatedGlossaryItem = {
          ...initialData,
          blueprint_id: newBlueprint.id,
          blueprint_version: newBlueprintVersion,
        };

        // Save the updated glossary item to the API
        await updateObject(
          {
            id: updatedGlossaryItem.id,
            game_id: updatedGlossaryItem.game_id,
            name: updatedGlossaryItem.name,
            type: updatedGlossaryItem.type || "Library Object",
            tags: updatedGlossaryItem.tags || [],
            data: updatedGlossaryItem.data,
            blueprint_id: newBlueprint.id,
            version: updatedGlossaryItem.version,
            blueprint_version: newBlueprintVersion,
            identifiers: updatedGlossaryItem.identifiers,
          },
          "patch",
          undefined,
          toast
        );
      }

      toast({
        title: "Template Created & Item Updated",
        description: `New template "${blueprintName}" created and object updated successfully.`,
      });
    } catch (error) {
      console.error("Error creating new template:", error);
      toast({
        title: "Error",
        description: "Failed to create new template",
        variant: "destructive",
      });
    }
  }, [gameId, glossaryState, initialData, toast]);

  /**
   * Handles saving the glossary item
   */
  const handleSave = useCallback(async () => {
    // Update ref whenever this function is called
    saveHandlerRef.current = handleSave;

    if (!gameId?.id || !glossaryState.selectedBlueprint) {
      toast({
        title: "Error",
        description: "Missing game ID or blueprint data",
        variant: "destructive",
      });
      return;
    }
    // Check if there are blueprint version mismatches and show confirmation
    if (
      blueprintChanges &&
      blueprintChanges.hasChanges &&
      wizardMode === "edit"
    ) {
      // Get the blueprint version for saving
      const newBlueprintVersion = getBlueprintVersionForSaving(
        glossaryState.selectedBlueprint
      );
      const currentBlueprintVersionDisplay =
        formatVersionForDisplay(newBlueprintVersion);
      const itemBlueprintVersionDisplay = formatVersionForDisplay(
        initialData?.blueprint_version
      );
      const confirmDialog = confirmation({
        title: "Update Template Version",
        description: `This object is using template version ${itemBlueprintVersionDisplay}, but the template is now at version ${currentBlueprintVersionDisplay}. 

The following changes will be applied: ${getChangeSummary(blueprintChanges)}

Choose how to save:
• "Update & Save" - Apply template changes and update to v${currentBlueprintVersionDisplay}
• "Save in Current Version" - Save your edits but keep template version ${itemBlueprintVersionDisplay} (object remains out of sync)`,
        confirmText: "Update & Save",
        alternativeText: "Save in Current Version",
        cancelText: "Cancel",
        variant: "default",
        onConfirm: async () => {
          confirmDialog.dismiss();

          try {
            // Step 1: Rebuild from the current blueprint to get the NEW structure
            // This ensures we have all new fields, removed old fields, and updated field definitions
            // It also preserves existing values where field IDs match
            const { sections: rebuiltSections } =
              rebuildGlossaryItemFromBlueprint(glossaryState.selectedBlueprint, initialData);

            // Step 2: Merge current form edits with the rebuilt structure
            // Get current parsed sections to capture any edits user made before clicking save
            const currentSections = glossaryState.parsedSections;

            // Create a map of current field values
            const currentFieldValues = new Map<string, any>();
            currentSections.forEach((section: sectionDataObject) => {
              section.fields.forEach((field) => {
                currentFieldValues.set(field.id, field.value);
              });
            });

            // Apply current field values to the rebuilt structure
            const finalSections = rebuiltSections.map(section => ({
              ...section,
              fields: section.fields.map(field => {
                // If user edited this field, use their edited value
                if (currentFieldValues.has(field.id)) {
                  return {
                    ...field,
                    value: currentFieldValues.get(field.id)
                  };
                }
                return field;
              })
            }));

            // Step 3: Update state with the final migrated structure
            glossaryState.updateSections(finalSections);

            // Step 4: Get the glossary item data with the new structure and edits
            // Pass true to update the blueprint version to the current template version
            const glossaryItemData = glossaryState.getGlossaryItemData(finalSections, true);

            // Step 5: Save the migrated object
            await onUpdate?.(glossaryItemData);

            // Step 6: Update the tracked blueprint version to reflect the save
            setCurrentItemBlueprintVersion(newBlueprintVersion);

            // Update the initialData to reflect the migration
            if (initialData) {
              initialData.blueprint_version = newBlueprintVersion;
              initialData.data = glossaryItemData.data;
            }

            // Clear blueprint changes since they've been applied
            setBlueprintChanges(null);

            // Mark as saved
            unsavedChanges.markAsSaved();
            clearDraft?.(); // Clear draft after successful save

            // CRITICAL: Notify tab context that there are no unsaved changes
            if (onUnsavedChangesChange) {
              onUnsavedChangesChange(false, async () => { });
            }

            // Show success toast
            toast({
              title: "Template Version Updated",
              description: `Object updated to template version ${currentBlueprintVersionDisplay}`,
              variant: "default",
            });

            // If inline mode, stay in edit mode; if modal mode, close
            if (inline) {
              // Stay in edit mode - fields will be locked/unlocked based on canEdit
              setViewDisplayMode("structured");
            } else {
              onClose?.();
            }
          } catch (error) {
            console.error("Error applying template update:", error);
            toast({
              title: "Error",
              description: "Failed to apply template update",
              variant: "destructive",
            });
          }
        },
        onAlternative: async () => {
          // Pass false to keep the old blueprint version
          const glossaryItemData = glossaryState.getGlossaryItemData(undefined, false);
          confirmDialog.dismiss();

          // Save without updating blueprint version
          await onUpdate?.(glossaryItemData);

          // DO NOT update the tracked blueprint version - keep the mismatch
          // DO NOT clear blueprint changes - keep the warning

          // Mark as saved
          unsavedChanges.markAsSaved();
          clearDraft?.(); // Clear draft after successful save

          // CRITICAL: Notify tab context that there are no unsaved changes
          if (onUnsavedChangesChange) {
            onUnsavedChangesChange(false, async () => { });
          }

          // Show success toast
          toast({
            title: "Saved Successfully",
            description: `Object saved with current template version ${itemBlueprintVersionDisplay}. Object remains out of sync with latest template.`,
            variant: "default",
          });

          // If inline mode, stay in edit mode; if modal mode, close
          if (inline) {
            // Stay in edit mode - fields will be locked/unlocked based on canEdit
            setViewDisplayMode("structured");
          } else {
            onClose?.();
          }
        },
        onCancel: () => {
          confirmDialog.dismiss();
        },
      });
    } else {
      // No version mismatch, save normally
      try {
        const glossaryItemData = glossaryState.getGlossaryItemData();

        // Detect template upgrade for version history
        let changeSummary: string | undefined = undefined;
        if (
          wizardMode === "edit" &&
          initialData?.blueprint_version &&
          glossaryState.selectedBlueprint
        ) {
          const oldBlueprintVersion = extractBlueprintVersion(
            glossaryState.selectedBlueprint
          );
          const itemBlueprintVersion =
            typeof initialData.blueprint_version === "number"
              ? initialData.blueprint_version
              : typeof initialData.blueprint_version === "object" &&
                "major" in initialData.blueprint_version
                ? initialData.blueprint_version.major * 1000000 +
                initialData.blueprint_version.minor * 1000 +
                initialData.blueprint_version.patch
                : 0;

          // Check if blueprint version changed
          if (oldBlueprintVersion !== itemBlueprintVersion) {
            const oldVersionStr = formatVersionForDisplay(
              initialData.blueprint_version
            );
            const newVersionStr = formatVersionForDisplay(
              glossaryState.selectedBlueprint.version as { major: number; minor: number; patch: number }
            );
            changeSummary = `Template upgraded from v${oldVersionStr} to v${newVersionStr}`;
          }
        }

        if (wizardMode === "create") {
          await onSave?.(glossaryItemData);
          // Mark as saved
          unsavedChanges.markAsSaved();
          clearDraft?.(); // Clear draft after successful save

          // CRITICAL: Notify tab context that there are no unsaved changes BEFORE closing
          // This prevents the tab system from showing the unsaved changes dialog
          if (onUnsavedChangesChange) {
            onUnsavedChangesChange(false, async () => { });
          }

          // Show appropriate success message based on status
          toast({
            title: "Success",
            description:
              glossaryItemData.status === "draft"
                ? "Object saved as draft. Complete all required fields to publish."
                : "Object created successfully",
          });

          if (inline) {
            // Stay in edit mode - fields will be locked/unlocked based on canEdit
            setViewDisplayMode("structured");
          } else {
            onClose?.();
          }
        } else if (wizardMode === "edit") {
          // Log template upgrade for debugging
          if (changeSummary) {
            console.log("Template upgrade detected:", changeSummary);
          }

          await onUpdate?.(glossaryItemData);

          // DO NOT update the blueprint version when saving normally
          // The blueprint_version is preserved from the original object
          // It will only be updated when migration is explicitly applied via the migration view

          // Mark as saved
          unsavedChanges.markAsSaved();
          clearDraft?.(); // Clear draft after successful save

          // CRITICAL: Notify tab context that there are no unsaved changes
          // This prevents the tab system from showing the unsaved changes dialog
          if (onUnsavedChangesChange) {
            onUnsavedChangesChange(false, async () => { });
          }

          // Show appropriate success message based on status
          toast({
            title: "Success",
            description:
              glossaryItemData.status === "draft"
                ? "Changes saved as draft. Complete all required fields to publish."
                : "Object updated successfully",
          });

          // If inline mode, stay in edit mode; if modal mode, close
          if (inline) {
            // Stay in edit mode - fields will be locked/unlocked based on canEdit
            setViewDisplayMode("structured");
          } else {
            onClose?.();
          }
        }
      } catch (error) {
        console.error("GlossaryWizard: Error in save process:", error);
        toast({
          title: "Error",
          description:
            error instanceof Error ? error.message : "Failed to save object",
          variant: "destructive",
        });
      }
    }
  }, [
    gameId,
    glossaryState,
    blueprintChanges,
    wizardMode,
    initialData,
    inline,
    confirmation,
    onUpdate,
    setCurrentItemBlueprintVersion,
    setBlueprintChanges,
    unsavedChanges,
    toast,
    onSave,
    onClose,
    setViewDisplayMode,
    clearDraft,
  ]);

  /**
   * Handles deletion of the glossary item with confirmation
   */
  const handleDelete = useCallback(() => {
    if (!canEdit) {
      toast({
        title: "Permission Denied",
        description: "You don't have permission to delete this item",
        variant: "destructive",
      });
      return;
    }

    if (!initialData?.id || !gameId) {
      toast({
        title: "Error",
        description: "Cannot delete: Missing item ID or game ID",
        variant: "destructive",
      });
      return;
    }

    const confirmDialog = confirmation({
      title: "Delete Library Object",
      description: `Are you sure you want to delete "${initialData.name || "this library object"
        }"? This action cannot be undone.`,
      confirmText: "Delete",
      cancelText: "Cancel",
      variant: "destructive",
      onConfirm: async () => {
        try {
          await deleteObject(initialData.id, gameId.id, toast);
          confirmDialog.dismiss();
          onClose?.(); // Close the wizard after successful deletion
        } catch (error) {
          console.error("Error deleting library object:", error);
          confirmDialog.dismiss();
          // Error toast is already shown by deleteObject
        }
      },
      onCancel: () => {
        confirmDialog.dismiss();
      },
    });
  }, [canEdit, initialData, gameId, confirmation, toast, onClose]);

  /**
   * Proceed with cancel action
   */
  const proceedWithCancel = useCallback(() => {
    console.log('[ObjectWizard] proceedWithCancel called');
    console.log('[ObjectWizard] onClose available:', !!onClose);

    // If in inline mode and editing, reset form and stay in edit mode
    if (inline && wizardMode === "edit") {
      console.log('[ObjectWizard] Resetting form (inline edit)');
      // Reset form to original data
      glossaryState.resetForm();
      setViewDisplayMode("structured");
      unsavedChanges.reset();
      // Stay in edit mode - fields will be locked/unlocked based on canEdit
    } else {
      console.log('[ObjectWizard] Closing wizard (modal mode)');
      // Modal mode - close
      glossaryState.resetForm();
      unsavedChanges.reset();
      if (onClose) {
        console.log('[ObjectWizard] Calling onClose');
        onClose();
      } else {
        console.warn('[ObjectWizard] onClose is not defined!');
      }
    }
  }, [inline, wizardMode, glossaryState, setViewDisplayMode, unsavedChanges, onClose]);

  /**
   * Cancels the form and closes the modal or switches to view mode
   * In inline mode, delegates to the tab system for unsaved changes handling
   */
  const handleFormCancel = useCallback(() => {
    console.log('[ObjectWizard] Cancel button clicked');
    console.log('[ObjectWizard] Current mode:', wizardMode);
    console.log('[ObjectWizard] Inline mode:', inline);

    // Check for unsaved changes
    const hasChanges = unsavedChanges.checkForChanges();
    console.log('[ObjectWizard] Has unsaved changes:', hasChanges);

    // In inline mode, let the tab system handle the unsaved changes dialog
    // This prevents showing two dialogs (one from wizard, one from tab system)
    if (inline) {
      console.log('[ObjectWizard] Inline mode - delegating to tab system');

      // Ensure the tab system knows about unsaved changes before calling onClose
      // (the useEffect might not have run yet due to async state updates)
      if (onUnsavedChangesChange && (wizardMode === "edit" || wizardMode === "create")) {
        const stableSaveHandler = async () => {
          if (saveHandlerRef.current) {
            await saveHandlerRef.current();
          }
        };
        const discardHandler = () => {
          clearDraft?.();
        };
        onUnsavedChangesChange(hasChanges, stableSaveHandler, discardHandler);
      }

      if (onClose) {
        onClose();
      }
      return;
    }

    // Modal mode - show wizard's own unsaved changes dialog
    if (
      hasChanges &&
      (wizardMode === "edit" || wizardMode === "create")
    ) {
      console.log('[ObjectWizard] Showing unsaved changes dialog');
      setPendingAction("cancel");
      setShowUnsavedDialog(true);
      return;
    }

    // No unsaved changes, proceed with cancel
    console.log('[ObjectWizard] No unsaved changes, proceeding with cancel');
    proceedWithCancel();
  }, [wizardMode, inline, unsavedChanges, onUnsavedChangesChange, onClose, setPendingAction, setShowUnsavedDialog, proceedWithCancel]);

  /**
   * Handles blueprint selection change
   */
  const handleBlueprintChange = useCallback((blueprintId: string) => {
    if (blueprintId) {
      const blueprintData = glossaryBlueprints.find(
        (bp) => bp.id === blueprintId
      );
      if (blueprintData) {
        glossaryState.updateBlueprint(blueprintData);
      }
    } else {
      glossaryState.setSelectedBlueprint(null);
      glossaryState.setSelectedBlueprintId("");
    }
  }, [glossaryBlueprints, glossaryState]);

  /**
   * Updates sections when form data changes
   */
  const handleSectionsChange = useCallback(
    (sections: sectionDataObject[]) => {
      // Always update sections - the conditional logic was preventing field updates
      glossaryState.updateSections(sections);
    },
    [glossaryState]
  );

  /**
   * Handle applying migration - rebuilds structure from new blueprint and saves with preserved data
   */
  const handleApplyMigration = useCallback(
    async (fieldUpdates: Map<string, Map<string, any>>) => {
      if (!gameId?.id || !glossaryState.selectedBlueprint || !initialData) {
        toast({
          title: "Error",
          description: "Missing game ID, blueprint data, or object data",
          variant: "destructive",
        });
        return;
      }

      try {
        // Step 1: Rebuild from the current blueprint to get the NEW structure
        // This ensures we have all new fields, removed old fields, and updated field definitions
        // It also preserves existing values where field IDs match
        const { sections: rebuiltSections, formData: rebuiltFormData } =
          rebuildGlossaryItemFromBlueprint(glossaryState.selectedBlueprint, initialData);

        // Step 2: Merge any field updates made during the migration preview
        // The fieldUpdates contains any edits the user made during migration preview
        // This works for both existing fields and newly added fields
        let finalSections = rebuiltSections;

        if (fieldUpdates && fieldUpdates.size > 0) {
          // Apply the field updates to the rebuilt structure
          finalSections = rebuiltSections.map(section => {
            const sectionUpdates = fieldUpdates.get(section.id);
            if (!sectionUpdates || sectionUpdates.size === 0) {
              return section;
            }

            return {
              ...section,
              fields: section.fields.map(field => {
                // If this field was edited during migration preview, use the edited value
                if (sectionUpdates.has(field.id)) {
                  return {
                    ...field,
                    value: sectionUpdates.get(field.id)
                  };
                }
                return field;
              })
            };
          });
        }

        // Step 3: Update state with the final migrated structure
        glossaryState.updateSections(finalSections);

        // Step 4: Get the current blueprint version for saving
        const newBlueprintVersion = getBlueprintVersionForSaving(
          glossaryState.selectedBlueprint
        );

        // Step 5: Get the glossary item data with the new structure and any edits
        // IMPORTANT: Pass finalSections directly to avoid reading stale state
        // Pass true to update the blueprint version to the current template version
        const glossaryItemData = glossaryState.getGlossaryItemData(finalSections, true);

        // Step 6: Save the migrated object
        await onUpdate?.(glossaryItemData);

        // Step 7: Update the tracked blueprint version to reflect the save
        if (newBlueprintVersion !== null && newBlueprintVersion !== undefined) {
          setCurrentItemBlueprintVersion(newBlueprintVersion);

          // Update the initialData to reflect the migration
          // This ensures the version mismatch detection uses the updated version
          if (initialData) {
            initialData.blueprint_version = newBlueprintVersion;
            // Also update the data to reflect the new structure
            initialData.data = glossaryItemData.data;
          }

          // Clear blueprint changes since they've been applied
          setBlueprintChanges(null);
        }

        // Mark as saved
        unsavedChanges.markAsSaved();
        clearDraft?.(); // Clear draft after successful save

        // Show success toast
        toast({
          title: "Migration Applied",
          description: "Object has been migrated to the latest blueprint version successfully",
          variant: "default",
        });
      } catch (error) {
        console.error("Error applying migration:", error);
        toast({
          title: "Error",
          description: "Failed to apply migration",
          variant: "destructive",
        });
      }
    },
    [
      gameId,
      glossaryState,
      onUpdate,
      setCurrentItemBlueprintVersion,
      initialData,
      setBlueprintChanges,
      unsavedChanges,
      toast,
      clearDraft,
    ]
  );

  /**
   * Handle view mode change
   */
  const handleViewModeChange = useCallback((mode: ViewDisplayMode) => {
    setViewDisplayMode(mode);
    // Code preview is now handled by the codePreview useMemo hook
    // No need to trigger download here
  }, [setViewDisplayMode]);

  /**
   * Handle backdrop click (modal only)
   */
  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleFormCancel();
    }
  }, [handleFormCancel]);

  /**
   * Reverts all changes back to the saved version
   */
  const handleRevert = useCallback(() => {
    if (!initialData) return;

    console.log('[ObjectWizard] Reverting to saved version');

    // Reset the form to the original data
    glossaryState.resetForm();

    // Clear unsaved changes
    unsavedChanges.reset();

    // Clear any draft
    clearDraft?.();

    // Notify tab context that there are no unsaved changes
    if (onUnsavedChangesChange) {
      onUnsavedChangesChange(false, async () => { });
    }

    // Show success toast
    toast({
      title: "Changes Reverted",
      description: "All changes have been discarded and the object has been reset to its saved state.",
    });
  }, [initialData, glossaryState, unsavedChanges, clearDraft, onUnsavedChangesChange, toast]);

  return {
    handleEdit,
    handleTitleChange,
    handleRemoveFields,
    handleCreateNewBlueprint,
    handleSave,
    handleDelete,
    handleFormCancel,
    handleRevert,
    proceedWithCancel,
    handleBlueprintChange,
    handleSectionsChange,
    handleViewModeChange,
    handleBackdropClick,
    handleApplyMigration,
    saveHandlerRef,
  };
}

